/* Source: /tmp/jacbuilder-deploy-scratch/deploy-8asygg26/features/graph/GraphTreePanel.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { HugeiconsIcon } from "@hugeicons/react";
import { InformationCircleIcon, ArrowRight01Icon, ArrowLeft01Icon } from "@hugeicons/core-free-icons";
import { EmptyPanel, friendly_time } from "../../components/shared/Display.js";
import { cn } from "../../lib/utils.js";
import { edge_phrase, badge_tone, badge_hint, short_id } from "./graph_helpers.js";
import { _jac } from "@jac/prelude";
function ProvenanceBadge(props) {
  const {badge} = props;
  return __jacJsx("span", {"title": badge_hint(badge), "className": cn("inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide", badge_tone(badge))}, [badge]);
}
function FieldList(props) {
  const {title, items} = props;
  return __jacJsx("section", {"className": "flex flex-col gap-2"}, [__jacJsx("h4", {"className": "text-xs font-semibold uppercase tracking-wide text-muted-foreground"}, [title]), (() => {
    let __jac_view_kids = [false];
    if ((items.length === 0)) {
      __jac_view_kids[0] = __jacJsx("p", {"className": "text-sm text-muted-foreground"}, ["Nothing recorded."]);
      return __jacJsx(null, {}, __jac_view_kids);
    }
    return __jacJsx(null, {}, __jac_view_kids);
  })(), __jacJsx("dl", {"className": "grid grid-cols-1 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)]"}, [(() => {
    let __jac_view_kids_1 = [false];
    __jac_view_kids_1[0] = (() => {
      let __jac_view_kids_2 = [];
      for (const f of items) {
        __jac_view_kids_2.push(__jacJsx("div", {"key": f.name, "className": "contents"}, [__jacJsx("dt", {"className": "font-mono text-xs text-muted-foreground sm:pt-0.5"}, [f.name]), __jacJsx("dd", {"className": cn("min-w-0 break-words", ((f.value === "not recorded") ? "text-muted-foreground italic" : ""))}, [f.value])]));
      }
      return __jac_view_kids_2;
    })();
    return __jacJsx(null, {}, __jac_view_kids_1);
  })()])]);
}
function EdgeRow(props) {
  const {link, other, outgoing, selfLabel, onSelect} = props;
  let other_id = (outgoing ? link.to_id : link.from_id);
  let other_label = (other ? other.label : "Record outside this incident");
  let other_kind = (other ? other.kind : "Unknown");
  function handle_click(e) {
    if (other) {
      onSelect(other_id);
    }
  }
  return __jacJsx("li", {"className": cn("rounded-md border bg-background", (link.derived ? "border-dashed" : "border-border"))}, [__jacJsx("button", {"type": "button", "onClick": handle_click, "disabled": !other, "className": "flex w-full flex-col gap-1.5 p-3 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"}, [__jacJsx("div", {"className": "flex flex-wrap items-center gap-2"}, [__jacJsx(HugeiconsIcon, {"icon": (outgoing ? ArrowRight01Icon : ArrowLeft01Icon), "strokeWidth": 2, "className": "size-4 text-muted-foreground"}, []), __jacJsx("span", {"className": "font-mono text-xs font-semibold text-primary"}, [link.kind]), __jacJsx(ProvenanceBadge, {"badge": link.status}, [])]), __jacJsx("p", {"className": "text-sm"}, [(outgoing ? (((selfLabel + " ") + edge_phrase(link.kind)) + " ") : ""), __jacJsx("span", {"className": "font-medium"}, [other_label]), (outgoing ? "" : ((" " + edge_phrase(link.kind)) + " this record"))]), __jacJsx("span", {"className": "font-mono text-[11px] text-muted-foreground"}, [((other_kind + " ") + short_id(other_id))]), ((link.fields.length > 0) && __jacJsx("span", {"className": "text-xs text-muted-foreground"}, [link.fields.map(f => ((f.name + ": ") + f.value)).join(", ")]))])]);
}
function RecordInspector(props) {
  const {graph, record, onSelect} = props;
  if ((record == null)) {
    return __jacJsx(EmptyPanel, {"icon": InformationCircleIcon, "title": "Select a record", "text": "Pick any record on the left to see its fields, where it came from, and how it connects."}, []);
  }
  let rid = record.id;
  let by_id = {};
  for (const n of graph.nodes) {
    by_id[n.id] = n;
  }
  let outgoing = graph.edges.filter(e => (e.from_id === rid)).map(e => e);
  let incoming = graph.edges.filter(e => (e.to_id === rid)).map(e => e);
  return __jacJsx("article", {"className": "flex flex-col gap-5 rounded-lg border border-border bg-card p-4 shadow-sm md:p-5"}, [__jacJsx("header", {"className": "flex flex-col gap-2 border-b border-border pb-4"}, [__jacJsx("div", {"className": "flex flex-wrap items-center gap-2"}, [__jacJsx("span", {"className": "rounded-sm bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary"}, [record.kind]), (() => {
    let __jac_view_kids_3 = [false];
    __jac_view_kids_3[0] = (() => {
      let __jac_view_kids_4 = [];
      for (const b of record.badges) {
        __jac_view_kids_4.push(__jacJsx(ProvenanceBadge, {"key": b, "badge": b}, []));
      }
      return __jac_view_kids_4;
    })();
    return __jacJsx(null, {}, __jac_view_kids_3);
  })()]), __jacJsx("h3", {"className": "font-display text-xl leading-snug break-words"}, [record.label]), __jacJsx("p", {"className": "font-mono text-xs break-all text-muted-foreground"}, [("jid " + rid)])]), __jacJsx("div", {"className": "grid gap-5 lg:grid-cols-2"}, [__jacJsx(FieldList, {"title": "Fields", "items": record.fields}, []), __jacJsx(FieldList, {"title": "Provenance", "items": record.provenance}, [])]), __jacJsx("section", {"className": "flex flex-col gap-2"}, [__jacJsx("h4", {"className": "text-xs font-semibold uppercase tracking-wide text-muted-foreground"}, [(("Outgoing relationships (" + String(outgoing.length)) + ")")]), (() => {
    let __jac_view_kids_5 = [false];
    if ((outgoing.length === 0)) {
      __jac_view_kids_5[0] = __jacJsx("p", {"className": "text-sm text-muted-foreground"}, ["No outgoing edges."]);
      return __jacJsx(null, {}, __jac_view_kids_5);
    }
    return __jacJsx(null, {}, __jac_view_kids_5);
  })(), __jacJsx("ul", {"className": "flex flex-col gap-2"}, [(() => {
    let __jac_view_kids_6 = [false];
    __jac_view_kids_6[0] = (() => {
      let __jac_view_kids_7 = [];
      for (const e of outgoing) {
        __jac_view_kids_7.push(__jacJsx(EdgeRow, {"key": e.id, "link": e, "other": _jac.dict.get(by_id, e.to_id), "outgoing": true, "selfLabel": record.label, "onSelect": onSelect}, []));
      }
      return __jac_view_kids_7;
    })();
    return __jacJsx(null, {}, __jac_view_kids_6);
  })()])]), __jacJsx("section", {"className": "flex flex-col gap-2"}, [__jacJsx("h4", {"className": "text-xs font-semibold uppercase tracking-wide text-muted-foreground"}, [(("Incoming relationships (" + String(incoming.length)) + ")")]), (() => {
    let __jac_view_kids_8 = [false];
    if ((incoming.length === 0)) {
      __jac_view_kids_8[0] = __jacJsx("p", {"className": "text-sm text-muted-foreground"}, ["No incoming edges."]);
      return __jacJsx(null, {}, __jac_view_kids_8);
    }
    return __jacJsx(null, {}, __jac_view_kids_8);
  })(), __jacJsx("ul", {"className": "flex flex-col gap-2"}, [(() => {
    let __jac_view_kids_9 = [false];
    __jac_view_kids_9[0] = (() => {
      let __jac_view_kids_10 = [];
      for (const e of incoming) {
        __jac_view_kids_10.push(__jacJsx(EdgeRow, {"key": e.id, "link": e, "other": _jac.dict.get(by_id, e.from_id), "outgoing": false, "selfLabel": record.label, "onSelect": onSelect}, []));
      }
      return __jac_view_kids_10;
    })();
    return __jacJsx(null, {}, __jac_view_kids_9);
  })()])])]);
}
/*jac:refresh-boundary*/;
export {EdgeRow, FieldList, ProvenanceBadge, RecordInspector};
//# sourceMappingURL=GraphTreePanel.js.map
